import { motion } from 'framer-motion';
import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { MapLayer } from '../types';

interface LayerSelectorProps {
  mapData: {
    id: string;
    layers: MapLayer[];
  };
  onToggleLayer: (layerId: string) => void;
  onStyleChange: (style: 'color' | 'gray' | 'simple') => void;
  currentStyle: 'color' | 'gray' | 'simple';
}

const TrafficIcon = () => (
  <img src="https://i.imgur.com/lZXFwnk.png" alt="Traffic Icon" width="20" height="20" />
);

const Zone30Icon = () => (
  <img src="https://i.imgur.com/SkNWIu3.png" alt="Traffic Icon" width="20" height="20" />
);

const DirectionIcon = () => (
  <img src="https://i.imgur.com/7liyja9.png" alt="Traffic Icon" width="20" height="20" />
);

const pistaciclavel = () => (
  <img src="https://i.imgur.com/L50g20W.png" alt="Traffic Icon" width="20" height="20" />
);

const faixaciclavel = () => (
  <img src="https://i.imgur.com/4T3QDgl.png" alt="Traffic Icon" width="20" height="20" />
);

const viapartilhada = () => (
  <img src="https://i.imgur.com/cfTVliz.png" alt="Traffic Icon" width="20" height="20" />
);

const manutencao = () => (
  <img src="https://i.imgur.com/RWEj7hP.png" alt="Traffic Icon" width="20" height="20" />
);

const perigos = () => (
  <img src="https://i.imgur.com/73P1O42.png" alt="Traffic Icon" width="20" height="20" />
);

const estacaogira = () => (
  <img src="https://i.imgur.com/oQw9wZf.png" alt="Traffic Icon" width="20" height="20" />
);

const estacionamentobicicleta = () => (
  <img src="https://i.imgur.com/ehq95cH.png" alt="Traffic Icon" width="20" height="20" />
);

const pontosdeinteresse = () => ( 
  <img src="https://i.imgur.com/QuuAsJ2.png" alt="Traffic Icon" width="20" height="20" />
);

const comboio = () => ( 
  <img src="https://i.imgur.com/UkqEIQh.png" alt="Traffic Icon" width="20" height="20" />
);

const metro = () => ( 
  <img src="https://i.imgur.com/s5bLGlt.png" alt="Traffic Icon" width="20" height="20" />
);

const mapabasecor = () => ( 
  <img src="https://i.imgur.com/zx7TLOf.png" alt="Traffic Icon" width="20" height="20" />
);

const mapabasecinza = () => ( 
  <img src="https://i.imgur.com/4mQJbGW.png" alt="Traffic Icon" width="20" height="20" />
);

const mapabasesimplificado = () => ( 
  <img src="https://i.imgur.com/A9P49Ro.png" alt="Traffic Icon" width="20" height="20" />
);

const zonas30 = () => ( 
  <img src="https://i.imgur.com/YCTNVMM.png" alt="Traffic Icon" width="20" height="20" />
);

const volumetransito = () => ( 
  <img src="https://i.imgur.com/C9C3HWv.png" alt="Traffic Icon" width="20" height="20" />
);

const legendastexto = () => ( 
  <img src="https://i.imgur.com/5vrLLsq.png" alt="Traffic Icon" width="20" height="20" />
);

const zonasverdes = () => ( 
  <img src="https://i.imgur.com/2l9JZ5l.png" alt="Traffic Icon" width="20" height="20" />
);

const legendSections = [
  {
    title: 'Mapa Base',
    items: [
      { id: 'style-color', name: 'Cor' , icon:mapabasecor },
      { id: 'style-gray', name: 'Cinza' , icon:mapabasecinza },
      { id: 'style-simple', name: 'Simplificado' , icon:mapabasesimplificado },
    ]
  },
  {
    title: 'Infraestrutura Ciclável',
    items: [
      { id: 'bike-lanes', name: 'Pista Ciclável\n(com separação física)', icon: pistaciclavel },
      { id: 'bike-paths', name: 'Faixa Ciclável\n(na faixa de rodagem)', icon: faixaciclavel },
      { id: 'shared-paths', name: 'Via Partilhada\n(em coexistência)', icon: viapartilhada },
      { id: 'direction', name: 'Sentido da marcha', icon: DirectionIcon }
    ]
  },
  {
    title: 'Elementos de Suporte',
    items: [
      { id: 'bike-repair', name: 'Manutenção/\nReparação de Bicicletas', icon:manutencao },
      { id: 'danger-zones', name: 'Perigos' , icon:perigos },
      { id: 'gira-stations', name: 'Estação GIRA\n(Bicicletas partilhadas)' , icon:estacaogira },
      { id: 'bike-parking', name: 'Estacionamento\n Bicicletas' , icon:estacionamentobicicleta },
      { id: 'points-of-interest', name: 'Pontos de interesse' , icon:pontosdeinteresse}
    ]
  },
  {
    title: 'Transportes Públicos',
    items: [
      { id: 'train', name: 'Comboio', icon: comboio },
      { id: 'metro', name: 'Metro', icon: metro }
    ]
  },
  {
    title: 'Zonas a Destacar',
    items: [
      { id: 'green-zones', name: 'Zonas Verdes' , icon:zonasverdes},
      { id: '30-zones', name: 'Zona 30 + Bicicletas' , icon:zonas30},
      { id: 'traffic', name: 'Volume de trânsito (zonas congestionadas)', icon: TrafficIcon }
    ]
  },
  {
    title: 'Texto',
    items: [
      { id: 'legenda-texto', name: 'Legendas de\n Texto' , icon:legendastexto},
    ]
  }
];

export default function LayerSelector({ mapData, onToggleLayer, onStyleChange, currentStyle }: LayerSelectorProps) {
  // Group layers by section
  const groupedLayers = mapData.layers.reduce((acc, layer) => {
    const section = layer.section || 'Outros';
    if (!acc[section]) {
      acc[section] = [];
    }
    acc[section].push(layer);
    return acc;
  }, {} as Record<string, MapLayer[]>);

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-lg shadow-lg h-full w-full min-h-[240px] flex flex-col relative"
      style={{ 
        height: 'calc(100vh - 320px)',
        maxHeight: '600px'
      }}
    >
      <div className="p-2 pb-0">
        <h3 className="text-sm font-semibold text-gray-900">Camadas de Informação</h3>
      </div>
      
      {/* Elevador da barra de rolagem personalizado */}
      <div className="absolute right-[8px] top-12 bottom-4 w-[12px] bg-gray-200 rounded-full z-10 cursor-pointer"
           onClick={(e) => {
             // Ignora cliques nos botões de seta
             if ((e.target as HTMLElement).closest('button')) return;
             
             const trackEl = e.currentTarget;
             const contentEl = trackEl.closest('.relative')?.querySelector('.overflow-y-auto') as HTMLElement;
             
             if (contentEl) {
               // Calcula a posição do clique relativa à trilha
               const clickY = e.clientY - trackEl.getBoundingClientRect().top;
               const trackHeight = trackEl.clientHeight;
               const clickPercent = clickY / trackHeight;
               
               // Calcula a nova posição de scroll
               const scrollHeight = contentEl.scrollHeight;
               const containerHeight = contentEl.clientHeight;
               const maxScrollTop = scrollHeight - containerHeight;
               const newScrollTop = maxScrollTop * clickPercent;
               
               // Aplica o scroll
               contentEl.scrollTop = newScrollTop;
             }
           }}>
        <div className="absolute w-full h-[25%] bg-blue-500 rounded-full shadow-md" 
             style={{top: '0%'}}>
          {/* Seta para cima - agora no topo do elevador */}
          <button 
            className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/3 w-6 h-6 flex items-center justify-center cursor-pointer hover:scale-110 transition-transform"
            onClick={(e) => {
              e.stopPropagation();
              const contentEl = e.currentTarget.closest('.relative')?.querySelector('.overflow-y-auto') as HTMLElement;
              if (contentEl) {
                // Rola 50px para cima
                contentEl.scrollBy({top: -50, behavior: 'smooth'});
              }
            }}
          >
            <div className="w-0 h-0 border-l-[4px] border-r-[4px] border-b-[5px] border-solid border-l-transparent border-r-transparent border-b-white drop-shadow-sm"></div>
          </button>
          
          {/* Seta para baixo - agora na base do elevador */}
          <button 
            className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/3 w-6 h-6 flex items-center justify-center cursor-pointer hover:scale-110 transition-transform"
            onClick={(e) => {
              e.stopPropagation();
              const contentEl = e.currentTarget.closest('.relative')?.querySelector('.overflow-y-auto') as HTMLElement;
              if (contentEl) {
                // Rola 50px para baixo
                contentEl.scrollBy({top: 50, behavior: 'smooth'});
              }
            }}
          >
            <div className="w-0 h-0 border-l-[4px] border-r-[4px] border-t-[5px] border-solid border-l-transparent border-r-transparent border-t-white drop-shadow-sm"></div>
          </button>
        </div>
      </div>
      
      <div className="p-2 overflow-y-auto pr-5 flex-grow" 
        onScroll={(e) => {
          const container = e.currentTarget;
          const scrollTop = container.scrollTop;
          const scrollHeight = container.scrollHeight;
          const containerHeight = container.clientHeight;
          const scrollPercentage = scrollTop / (scrollHeight - containerHeight);
          
          const thumb = container.parentElement?.querySelector('.bg-blue-500') as HTMLElement;
          if (thumb) {
            const maxTop = 100 - (containerHeight / scrollHeight * 100);
            thumb.style.top = `${scrollPercentage * maxTop}%`;
          }
        }}
      >
        {legendSections.map((section) => (
          <div key={section.title} className="mb-2 last:mb-0 mr-1">
            <div className="flex justify-between items-center mb-1">
              <h4 className="text-xs font-semibold text-gray-700">{section.title}</h4>
            </div>
            
            <div className="space-y-1 pl-1">
              {section.items.map((item) => {
                const layer = mapData.layers.find(l => l.id === item.id);
                const isActive = item.id.startsWith('style-')
                  ? item.id === `style-${currentStyle}`
                  : layer?.isActive || false;
                
                // Verifica se o item deve ser desabilitado
                const isDisabled = 
                  (mapData.id === 'city' && item.id === 'direction') || // Desabilita "Sentido da marcha" no mapa da cidade
                  (mapData.id === 'street' && item.id === '30-zones') || // Desabilita "Zona 30 + Bicicletas" no mapa da rua
                  (mapData.id === 'street' && item.id === 'train') || // Desabilita "Comboio" no mapa da rua
                  (mapData.id === 'street' && item.id === 'legenda-texto' && currentStyle !== 'simple') || // Desabilita "Legendas de Texto" na vista rua quando não está no mapa simplificado
                  false;
                
                // Manipulação para estilo do mapa
                const handleClick = () => {
                  if (isDisabled) return; // Não faz nada se estiver desabilitado
                  
                  if (item.id.startsWith('style-')) {
                    onStyleChange(item.id.replace('style-', '') as 'color' | 'gray' | 'simple');
                  } else {
                    onToggleLayer(item.id);
                  }
                };
                
                return (
                  <div 
                    key={item.id}
                    className={`flex items-center text-xs rounded-md py-1 pr-1 ${isActive ? 'bg-blue-50' : 'hover:bg-gray-50'} cursor-${isDisabled ? 'not-allowed' : 'pointer'} transition-colors`}
                    onClick={handleClick}
                  >
                    <div className="flex-shrink-0 w-4 h-4 ml-1 mr-0.5">
                      <input
                        type="checkbox"
                        checked={isActive}
                        onChange={handleClick}
                        disabled={isDisabled}
                        className={`w-3 h-3 ${isDisabled ? 'opacity-50' : 'accent-blue-500'}`}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="flex-shrink-0 w-4 h-4 mx-0.5">
                      {item.icon && <item.icon />}
                    </div>
                    <div className={`ml-1 ${isActive ? 'font-medium text-blue-600' : isDisabled ? 'text-gray-400' : 'text-gray-600'} flex-grow leading-tight`} style={{ fontSize: '0.65rem', maxWidth: 'calc(100% - 25px)' }}>
                      {item.name}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
}